import {
  useState, useCallback, useEffect, useReducer,
} from 'react';
import {
  Modal,
  ModalStepperMenu,
  ModalStepperContent,
  Stepper,
} from '@visionplanner/ui-react-material';
import GeneralInformation from './steps/general-information';
import OnBoardingInformation from './steps/onboarding-information';
import Review from './steps/review';
import { SalesStepWrapper } from './sales-styles';
import { salesReducer, initialState, types } from './sales-onboarding-partner.reducer';
import { SalesOnBoarding } from './sales.context';
import * as requests from './sales-onboarding-partner.request';
import OnBoardGreeting from '../partner/on-board-greeting';
import { useDataSource } from '../../common/custom-hooks/use-data-source';
import { useAsyncCallback } from '../../common/custom-hooks/use-async-callback';
import { useModal } from '../../common/modal-dialog/modal-dialog-container';
import InvoiceInformation from './steps/invoice-information';

/** Component to onboard a new partner which is done
 *  by a sales people
 */

const SalesOnboardingPartner = ({ match: { params } }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [state, dispatch] = useReducer(salesReducer, initialState);
  const [endOnBoarding, setEndOnBoarding] = useState(false);

  const [fieldOptions] = useDataSource(requests.getFieldOptions);
  const [steps] = useDataSource(requests.getSalesSteps);

  const { showModal, hideModal } = useModal();

  const handleConfirmationModal = useCallback(() => {
    showModal('confirm', {
      title: 'Afsluiten zonder op te slaan?',
      children: 'Uw wijzigingen worden niet opgeslagen als u afsluit. Wilt u afsluiten zonder op te slaan?',
      handleConfirm: () => window.location.assign(`${visionplannerUrl}/crm`),
      dataTa: 'confirmation-modal',
      confirmText: 'Ja',
      cancelText: 'Nee',
      onCloseModal: () => hideModal('confirm'),
    });
  }, [showModal, hideModal]);

  const getStepContent = useCallback((stepIndex) => {
    const {
      officeTypes, invoiceTypes, originTypes,
    } = fieldOptions;
    switch (stepIndex) {
      case 1:
        return <OnBoardingInformation />;
      case 2:
        return <InvoiceInformation invoiceTypes={invoiceTypes} />;
      case 3:
        return <Review invoiceTypes={invoiceTypes} officeTypes={officeTypes} originTypes={originTypes} setEndOnBoarding={setEndOnBoarding} />;
      default:
        return (<GeneralInformation officeTypes={officeTypes} invoiceTypes={invoiceTypes} originTypes={originTypes} />);
    }
  }, [fieldOptions]);

  const fetchSalesDetails = useAsyncCallback(async () => {
    const salesDetails = await requests.getSalesViewDetails(params.id);
    dispatch({
      type: types.fetchSalesDetails,
      payload: salesDetails,
    });
  }, [params.id]);

  const handleTask = useCallback(async () => {
    if (params.id) {
      fetchSalesDetails();
    }
    setActiveStep(state.currentStep);
  }, [fetchSalesDetails, params.id, state.currentStep]);

  useEffect(() => {
    handleTask();
  }, [handleTask]);

  return (
    <SalesStepWrapper>
      {fieldOptions && steps && (
        <Modal
          onClose={handleConfirmationModal}
          title="Voeg een nieuwe partner toe"
          dataTa="sales-onboarding-partner"
          preventDismissalOnOutsideClick
          open
        >
          {endOnBoarding
            ? (
              <OnBoardGreeting
                dataTa="exit-wizard"
                onClick={() => {
                  window.location.assign(`${visionplannerUrl}/crm`);
                }}
                buttonLabel="Sluiten"
                imageUrl="/assets/images/end-onboarding.png"
                title="Gefeliciteerd !"
                description="Je hebt deze wizard succesvol afgerond.
                  De uitnodiging is naar de contactpersoon verstuurd."
              />
            )
            : (
              <div className="flex full-height overflow-hidden">
                <ModalStepperMenu className="overflow-auto">
                  <Stepper
                    activeStep={activeStep}
                    steps={steps.salesSteps}
                    activateStep={setActiveStep}
                  />
                </ModalStepperMenu>
                <ModalStepperContent>
                  <SalesOnBoarding.Provider value={{
                    setActiveStep,
                    activeStep,
                    state,
                    dispatch,
                  }}
                  >
                    {getStepContent(activeStep)}
                  </SalesOnBoarding.Provider>
                </ModalStepperContent>
              </div>
            )}
        </Modal>
      )}
    </SalesStepWrapper>
  );
};

export default SalesOnboardingPartner;
